// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\forms\Form7ContactPerson.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantLayout from "../../components/MerchantLayout";
import { apiGet, apiPut } from "../../api";

type Form7 = {
  // 自動反映（必須）※ただし編集可能
  contact_name: string;
  contact_phone: string;
  contact_email: string;

  // 手入力（任意）
  department_role: string;

  is_completed: boolean;
};

const empty: Form7 = {
  contact_name: "",
  contact_phone: "",
  contact_email: "",
  department_role: "",
  is_completed: false,
};

export default function Form7ContactPerson() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [data, setData] = useState<Form7>(empty);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // ✅ Completion rule (BUSINESS LOGIC: keep as-is)
  // - 必須（名前/電話/Email）+ Email形式
  const canComplete = useMemo(() => {
    return (
      data.contact_name.trim() !== "" &&
      data.contact_phone.trim() !== "" &&
      data.contact_email.trim() !== "" &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contact_email.trim())
    );
  }, [data.contact_name, data.contact_phone, data.contact_email]);

  // ✅ Step③ UI states
  const statusLabel = canComplete ? "入力済み" : "未完了";
  

  useEffect(() => {
    const run = async () => {
      setError("");
      setSuccess("");

      try {
        // 1) Load saved Form7 (if exists)
        let saved: any = null;
        try {
          const jsonSaved = await apiGet("/merchant/forms/contact-person");
          saved = jsonSaved?.data || null;
        } catch {
          saved = null;
        }

        // 2) Load merchant profile for 自動反映 fields
        const meJson = await apiGet("/merchant/me");

        // ✅ /merchant/me returns: { ok: true, merchant: {...} }
        const me =
          meJson?.merchant ||
          meJson?.data?.merchant ||
          meJson?.data ||
          meJson ||
          {};

        // Best-effort mapping (keep as-is)
        const autoName =
          me.contact_name ||
          me.name ||
          me.company_name ||
          me.merchant_name ||
          me.full_name ||
          me.staff_name ||
          "";

        const autoPhone =
          me.contact_phone ||
          me.phone ||
          me.mobile ||
          me.rep_phone_mobile ||
          me.tel ||
          "";

        const autoEmail = me.email || me.merchant_email || me.contact_email || "";

        setData({
          // ✅ priority: saved -> auto -> ""
          contact_name: (saved?.contact_name ?? autoName ?? "") || "",
          contact_phone: (saved?.contact_phone ?? autoPhone ?? "") || "",
          contact_email: (saved?.contact_email ?? autoEmail ?? "") || "",
          department_role: saved?.department_role || "",
          is_completed: Boolean(saved?.is_completed),
        });

        setLoading(false);
      } catch (e: any) {
        setError(
          e?.message ||
            "ご担当者様情報の取得に失敗しました。バックエンドが起動しているか確認してください。"
        );
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const onSave = async (): Promise<boolean> => {
    setError("");
    setSuccess("");

    if (data.contact_email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.contact_email.trim())) {
      setError("Emailの形式が正しくありません。");
      return false;
    }

    setSaving(true);

    try {
      const json = await apiPut("/merchant/forms/contact-person", {
        contact_name: data.contact_name,
        contact_phone: data.contact_phone,
        contact_email: data.contact_email,
        department_role: data.department_role,
        is_completed: canComplete,
      });

      setData((prev) => ({ ...prev, is_completed: canComplete }));

      const okCompleted = Boolean(json?.is_completed ?? canComplete);
      setSuccess(okCompleted ? "保存しました（入力済み）" : "保存しました（未完了）");
      return true;
    } catch (e: any) {
      setError(e?.message || "保存に失敗しました。バックエンドが起動しているか確認してください。");
      return false;
    } finally {
      setSaving(false);
    }
  };

    const onBack = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/store-list");
  };


  const onNext = async () => {
  const ok = await onSave();
  if (!ok) return;
  navigate("/merchant/register?section=uploads");
};

const onList = async () => {
  const ok = await onSave();
  if (!ok) return;
  navigate("/merchant/register");
};




  return (
    <MerchantLayout title="7. ご担当者様情報" active="forms">
      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: 12,
            background: "#ecfdf5",
            border: "1px solid #bbf7d0",
            color: "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {success}（いつでも編集できます）
        </div>
      )}

      {!loading && (
        <>
          {/* ✅ remove empty heading block (was blank) */}

          <div
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 12,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <div
              style={{
                padding: "10px 12px",
                fontWeight: 900,
                background: "#f8fafc",
                borderBottom: "1px solid #e2e8f0",
              }}
            >
              ご担当者様情報（自動反映）
            </div>

            <div style={{ padding: 12 }}>
              <div
                style={{
                  marginBottom: 12,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  color: "#1e3a8a",
                  padding: "10px 12px",
                  borderRadius: 12,
                  fontWeight: 900,
                  fontSize: 13,
                }}
              >
                「自動反映」項目はログイン情報から表示されます。（必要に応じて編集してください）
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
                  gap: 20,
                  alignItems: "start",
                }}
              >
                <Field label="窓口担当者名（自動反映）" required>
                  <input
                    style={inputStyle}
                    value={data.contact_name}
                    onChange={(e) => setData({ ...data, contact_name: e.target.value })}
                    placeholder="全角25字まで"
                  />
                </Field>

                <Field label="部署・役職">
                  <input
                    style={inputStyle}
                    value={data.department_role}
                    onChange={(e) => setData({ ...data, department_role: e.target.value })}
                    placeholder="全角25字まで"
                  />
                </Field>

                <Field label="担当者電話番号" required>
                  <input
                    style={inputStyle}
                    value={data.contact_phone}
                    onChange={(e) => setData({ ...data, contact_phone: e.target.value })}
                    placeholder="半角数字"
                  />
                </Field>

                <Field label="Email" required>
                  <input
                    style={inputStyle}
                    value={data.contact_email}
                    onChange={(e) => setData({ ...data, contact_email: e.target.value })}
                    placeholder="例）info@example.com"
                  />
                </Field>
              </div>
            </div>
          </div>

          {/* ✅ Step③: only ONE button + status */}
                    <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button
              onClick={onBack}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "戻る"}
            </button>

            <button
              onClick={onSave}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#1d4ed8",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>

            <button
  onClick={onList}
  disabled={saving}
  style={{
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    background: "#64748b",
    color: "#fff",
    fontWeight: 900,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    transition: "background 0.2s ease",
  }}
>
  一覧
</button>



            <button
  onClick={onNext}
  disabled={saving}
  style={{
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    background: "#dc2626",
    color: "#fff",
    fontWeight: 900,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    transition: "background 0.2s ease",
  }}
>
  {saving ? "保存中..." : "次へ"}
</button>
          </div>

          <div style={{ marginTop: 10, fontSize: 13, color: "#475569" }}>
            ステータス：{" "}
            <span style={{ fontWeight: 900, color: canComplete ? "#dc2626" : "#64748b" }}>
              {statusLabel}
            </span>
          </div>

          {!canComplete && (
            <div style={{ fontSize: 12, color: "#475569", fontWeight: 800 }}>
              ※ 必須項目が未入力のため「未完了」になります
            </div>
          )}
        </>
      )}
    </MerchantLayout>
  );
}

/** ✅ Match Form1 required label style */
function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>
      {children}
    </div>
  );
}

/** ✅ Shared styles (match Form1/Form2) */
const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};
